import { useForm } from "react-hook-form";
import { Member } from "src/redux/types/_customer.type";
import { BloodGroupFormValues, bloodGroupSchema } from "../schema/basic-info.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { memberKeys } from "../hooks/useFetchMembers";
import { Form, FormControl, FormField, FormItem, FormMessage } from "src/components/form/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/input/Select";
import { Button } from "src/components/buttons/shadcn-button";
import { useEditBloodGroup } from "../hooks/useEditBloodGroup";

const BLOOD_GROUPS = [
  "A+",
  "A-",
  "B+",
  "B-",
  "AB+",
  "AB-",
  "O+",
  "O-",
] as const;

type BloodGroupEditFormProps = {
  member: Member;
  subscriptionId: number;
  onDone: () => void;
  updateSelected: (member: Member) => void;
};

const BloodGroupEditForm = ({
  member,
  subscriptionId,
  onDone,
  updateSelected,
}: BloodGroupEditFormProps) => {

  const queryClient = useQueryClient();
  const form = useForm<BloodGroupFormValues>({
    resolver: zodResolver(bloodGroupSchema),
    defaultValues: {
      blood_group: member?.blood_group as BloodGroupFormValues["blood_group"],
    },
  });

  const { mutate: editBloodGroup, isPending } = useEditBloodGroup();


  const handleSubmit = (values: BloodGroupFormValues) => {
    editBloodGroup(
      { memberId: member.id, subscriptionId, values },
      {
        onSuccess: () => {
          const updatedMember = { ...member, ...values };

          updateSelected(updatedMember);

          queryClient.setQueryData(
            memberKeys.list(subscriptionId),
            (old: Member[] | undefined) =>
              old?.map((m) => (m.id === updatedMember.id ? updatedMember : m)),
          );

          onDone();
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex items-center gap-2"
      >
        <FormField
          control={form.control}
          name="blood_group"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select blood group" />
                  </SelectTrigger>
                  <SelectContent>
                    {BLOOD_GROUPS.map((bg) => (
                      <SelectItem key={bg} value={bg}>
                        {bg}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" size="sm" disabled={isPending}>
          {isPending ? "Saving..." : "Save"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onDone}
          disabled={isPending}
        >
          Cancel
        </Button>
      </form>
    </Form>
  );
};

export default BloodGroupEditForm